// src/pages/ClientDemographicsViewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Spinner, Alert, Row, Col, Card, Table, Badge } from "react-bootstrap";
import api from "../api";
import ClientContactLine from "../components/ClientContactLine";
import SalvationArmyBadge, {
  salvationArmyHelpIsCurrent,
} from "../components/SalvationArmyBadge";

const COUNTY_OPTIONS = [
  "Clare",
  "Gladwin",
  "Isabella",
  "Mecosta",
  "Midland",
  "Missaukee",
  "Osceola",
];

const normalizeDateInput = (val) => {
  if (!val) return "";
  return String(val).slice(0, 10);
};

const getHousingTypeLabel = (ht) => ht?.name || ht?.label || ht?.description || "";

// Small display helpers
function displayOrDash(v) {
  const s = v == null ? "" : String(v).trim();
  return s ? s : "—";
}
// Show only the last 4 digits of an SSN; never render it in full.
function maskSsn(v) {
  const digits = (v == null ? "" : String(v)).replace(/\D/g, "");
  if (!digits) return "—";
  return `•••-••-${digits.slice(-4)}`;
}
function ynOrDash(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "—";
}
function fmtDateOrDash(v) {
  const d = normalizeDateInput(v);
  return d || "—";
}

export default function ClientDemographicsViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Household members
  const [familyMembers, setFamilyMembers] = useState([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [fmError, setFmError] = useState("");

  // Housing Types
  const [housingTypes, setHousingTypes] = useState([]);
  const [housingTypesLoading, setHousingTypesLoading] = useState(false);

  // Landlords lookup list (for resolving landlord_id to landlord record)
  const [landlords, setLandlords] = useState([]);
  const [landlordsLoading, setLandlordsLoading] = useState(false);

  const fetchLandlords = async (q = "") => {
    setLandlordsLoading(true);
    try {
      const res = await api.get("/clients/landlords", { params: q ? { q } : {} });
      const list = Array.isArray(res.data) ? res.data : res.data.items || [];
      setLandlords(list);
    } catch (e) {
      setLandlords([]);
    } finally {
      setLandlordsLoading(false);
    }
  };

  // Load client + household members
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError("");
      setFmError("");

      try {
        setFmLoading(true);

        const [cRes, fmRes] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/family-members`),
        ]);

        setClient(cRes.data);
        setFamilyMembers(Array.isArray(fmRes.data) ? fmRes.data : []);
      } catch (err) {
        setError("Failed to load client");
      } finally {
        setFmLoading(false);
        setLoading(false);
      }
    };

    if (id) loadAll();
  }, [id]);

  // Load housing types on mount
  useEffect(() => {
    const loadHousingTypes = async () => {
      setHousingTypesLoading(true);
      try {
        const res = await api.get("/clients/housing-types");
        const list = Array.isArray(res.data) ? res.data : res.data.items || [];
        setHousingTypes(list);
      } catch (e) {
        setHousingTypes([]);
      } finally {
        setHousingTypesLoading(false);
      }
    };
    loadHousingTypes();
  }, []);

  // Load landlords on mount (so we can resolve landlord_id)
  useEffect(() => {
    fetchLandlords("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const housingTypeObj = useMemo(() => {
    if (!client?.housing_type) return null;
    return housingTypes.find((x) => String(x.id) === String(client.housing_type)) || null;
  }, [client?.housing_type, housingTypes]);

  const housingTypeLabel = useMemo(() => {
    return getHousingTypeLabel(housingTypeObj) || "";
  }, [housingTypeObj]);

  // Determine if landlord should show based on housing type label (same logic as edit)
  const shouldShowLandlord = useMemo(() => {
    const label = (housingTypeLabel || "").toLowerCase();
    return label === "rent" || label === "rent - multi-tenant";
  }, [housingTypeLabel]);

  const selectedLandlord = useMemo(() => {
    if (!client?.landlord_id) return null;

    // prefer embedded landlord object if API included it
    if (client?.landlord && (client.landlord.id || client.landlord.name)) return client.landlord;

    return landlords.find((l) => String(l.id) === String(client.landlord_id)) || null;
  }, [client?.landlord_id, client?.landlord, landlords]);

  const shouldShowWhereLaundry = useMemo(() => client?.has_washer_dryer === false, [client?.has_washer_dryer]);
  const shouldShowDenomination = useMemo(() => client?.wants_pastor_visit === true, [client?.wants_pastor_visit]);
  const shouldShowChurchName = useMemo(() => !!client?.attends_church_in_clare, [client?.attends_church_in_clare]);

  const handleBack = () => navigate("/clients");
  const handleEdit = () => navigate(`/clients/${id}`); // your current demographics edit page

  if (loading) return <Spinner animation="border" />;
  if (!client) return <Alert variant="danger">Client not found</Alert>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Demographics</h3>
          <ClientContactLine client={client} />
          <div className="text-muted">Client ID: {id}</div>
        </Col>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={handleBack}>
            Back to Clients
          </Button>
          <Button onClick={handleEdit}>Edit Demographics</Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {fmError && <Alert variant="danger">{fmError}</Alert>}

      <Card>
        <Card.Body>
          {/* Intake Date */}
          <Row className="align-items-end">
            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Intake Date
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span>{fmtDateOrDash(client.intake_date)}</span>
                  <SalvationArmyBadge client={client} size={22} />
                </div>
              </div>
            </Col>
          </Row>

          {/* Name */}
          <Row>
            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  First Name
                </div>
                <div>{displayOrDash(client.first_name)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Middle Name
                </div>
                <div>{displayOrDash(client.middle_name)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Last Name
                </div>
                <div>{displayOrDash(client.last_name)}</div>
              </div>
            </Col>
          </Row>

          {/* Birth Date + SSN + Phones */}
          <Row>
            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Birth Date
                </div>
                <div>{fmtDateOrDash(client.birth_date)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  SS#
                </div>
                <div>{maskSsn(client.ssn)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Home Phone
                </div>
                <div>{displayOrDash(client.home_phone)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Mobile Phone
                </div>
                <div>{displayOrDash(client.mobile_phone)}</div>
              </div>
            </Col>
          </Row>

          {/* Functional Limitations + Safety Alerts + Special Circumstances */}
          <Row>
            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Functional Limitations
                </div>
                <div>{displayOrDash(client.functional_limitations)}</div>
              </div>
            </Col>

            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Safety Alerts
                </div>
                <div>{displayOrDash(client.safety_alerts)}</div>
              </div>
            </Col>

            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Special Circumstances
                </div>
                <div>{displayOrDash(client.special_circumstances)}</div>
              </div>
            </Col>
          </Row>

          <div className="mb-3">
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>
              Driver Note
            </div>
            <div style={{ whiteSpace: "pre-wrap" }}>{displayOrDash(client.driver_notes)}</div>
          </div>

          {/* Received help from Salvation Army. The date is shown as recorded;
              only the badge stops appearing once it is over a year old. */}
          <Row>
            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Received Help From Salvation Army
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span>
                    {client.salvation_army_help
                      ? `Yes — ${fmtDateOrDash(client.salvation_army_help_date)}`
                      : "No"}
                  </span>
                  {client.salvation_army_help && !salvationArmyHelpIsCurrent(client) ? (
                    <Badge bg="secondary">Over 1 year</Badge>
                  ) : null}
                </div>
              </div>
            </Col>
          </Row>

          {/* Address */}
          <Row>
            <Col md={5} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Address Line 1
                </div>
                <div>{displayOrDash(client.address_line1)}</div>
              </div>
            </Col>

            <Col md={5} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Address Line 2
                </div>
                <div>{displayOrDash(client.address_line2)}</div>
              </div>
            </Col>
          </Row>

          {/* City/State/ZIP/County */}
          <Row>
            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  City
                </div>
                <div>{displayOrDash(client.city)}</div>
              </div>
            </Col>

            <Col md={2} sm={3}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  State
                </div>
                <div>{displayOrDash(client.state)}</div>
              </div>
            </Col>

            <Col md={2} sm={3}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  ZIP
                </div>
                <div>{displayOrDash(client.postal_code)}</div>
              </div>
            </Col>

            <Col md={3} sm={6}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  County
                </div>
                <div>
                  {COUNTY_OPTIONS.includes(client.county) ? client.county : displayOrDash(client.county)}
                </div>
              </div>
            </Col>
          </Row>

          {/* Housing Type */}
          <Row>
            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Housing Type
                </div>
                <div>
                  {housingTypesLoading ? (
                    <span className="text-muted">Loading…</span>
                  ) : housingTypeLabel ? (
                    housingTypeLabel
                  ) : (
                    "—"
                  )}
                </div>
              </div>
            </Col>
          </Row>

          {/* Landlord */}
          {shouldShowLandlord && (
            <Card className="mb-3">
              <Card.Body>
                <Row className="align-items-start">
                  <Col md={6} sm={12}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Landlord
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <div>{selectedLandlord?.name ? selectedLandlord.name : "—"}</div>
                      {landlordsLoading && <Badge bg="secondary">Loading</Badge>}
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      {selectedLandlord?.phone ? (
                        <>
                          <strong>Phone:</strong> {selectedLandlord.phone}
                        </>
                      ) : (
                        " "
                      )}
                    </div>
                    {(selectedLandlord?.address ||
                      selectedLandlord?.city ||
                      selectedLandlord?.state ||
                      selectedLandlord?.zipcode) && (
                      <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                        <strong>Address:</strong>{" "}
                        {[selectedLandlord.address, selectedLandlord.city, selectedLandlord.state, selectedLandlord.zipcode]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                  </Col>
                  <Col md={6} sm={12}>
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Note
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.95rem" }}>
                      Landlord shows only for Rent / Rent - Multi-tenant housing types.
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Flags */}
          <div className="mb-3">
            <div className="text-muted" style={{ fontSize: "0.9rem" }}>
              Flags
            </div>

            <div className="d-flex flex-wrap gap-2 mt-2">
              <Badge bg={client.lives_in_clare ? "success" : "secondary"}>Lives in Clare County</Badge>
              <Badge bg={client.attends_church_in_clare ? "success" : "secondary"}>
                Attends church in Clare County
              </Badge>
              <Badge bg={client.children_school_in_clare ? "success" : "secondary"}>
                Children attend school in Clare County
              </Badge>
              <Badge bg={client.permission_to_verify ? "success" : "secondary"}>
                Permission to verify information
              </Badge>
            </div>
          </div>

          {/* Church Name (only when attends church in Clare) */}
          {shouldShowChurchName && (
            <Row>
              <Col md={6} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Name of church
                  </div>
                  <div>{displayOrDash(client.church_name)}</div>
                </div>
              </Col>
            </Row>
          )}

          {/* Referred By */}
          <Row>
            <Col md={4} sm={12}>
              <div className="mb-3">
                <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                  Referred By
                </div>
                <div>{displayOrDash(client.referred_by)}</div>
              </div>
            </Col>
          </Row>

          {/* Household Members */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="mb-0">People living in the home</h5>
            </div>

            {fmLoading && <div className="text-muted mb-2">Loading household members…</div>}

            <Table bordered hover size="sm" responsive>
              <thead>
                <tr>
                  <th style={{ width: "22%" }}>Full Name</th>
                  <th style={{ width: "14%" }}>Relationship</th>
                  <th style={{ width: "12%" }}>Birth Date</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {familyMembers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-3">
                      No household members added yet.
                    </td>
                  </tr>
                ) : (
                  familyMembers.map((m, idx) => (
                    <tr key={m.id || `row-${idx}`}>
                      <td>{displayOrDash(m.full_name)}</td>
                      <td>{displayOrDash(m.relationship)}</td>
                      <td>{fmtDateOrDash(m.birth_date)}</td>
                      <td>{displayOrDash(m.notes)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Demographics */}
          <div className="mt-4">
            <h5 className="mb-3">Demographics</h5>

            <Row>
              <Col md={4} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Has Washer and Dryer
                  </div>
                  <div>{ynOrDash(client.has_washer_dryer)}</div>
                </div>
              </Col>
            </Row>

            {shouldShowWhereLaundry && (
              <Row>
                <Col md={6} sm={12}>
                  <div className="mb-3">
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Where do you do laundry?
                    </div>
                    <div>{displayOrDash(client.where_laundry)}</div>
                  </div>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={4} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    What transportation do you have?
                  </div>
                  <div>{displayOrDash(client.transportation_type)}</div>
                </div>
              </Col>
              <Col md={4} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Rider Category
                  </div>
                  <div>{displayOrDash(client.rider_category)}</div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={4} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Do you have a valid driver's license?
                  </div>
                  <div>{ynOrDash(client.has_valid_drivers_license)}</div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={3} sm={6}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Uses Transit
                  </div>
                  <div>{ynOrDash(client.uses_transit)}</div>
                </div>
              </Col>
              {client.uses_transit && (
                <Col md={3} sm={6}>
                  <div className="mb-3">
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Transit Fare
                    </div>
                    <div>{client.transit_fare ? `$${client.transit_fare} ride` : "—"}</div>
                  </div>
                </Col>
              )}
            </Row>

            {/* Pastor visit + denomination */}
            <Row>
              <Col md={4} sm={12}>
                <div className="mb-3">
                  <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                    Would you like a pastor to visit you?
                  </div>
                  <div>{ynOrDash(client.wants_pastor_visit)}</div>
                </div>
              </Col>
            </Row>

            {shouldShowDenomination && (
              <Row>
                <Col md={6} sm={12}>
                  <div className="mb-3">
                    <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                      Preferred Denomination
                    </div>
                    <div>{displayOrDash(client.church_denom_preferred)}</div>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </Card.Body>
      </Card>
    </>
  );
}
